/**
 * Revit Parameters Configuration
 *
 * Centralized definitions for all Revit parameter constants used throughout the application.
 * This allows for consistent parameter references and easy maintenance.
 */

/**
 * Categories for grouping Revit parameters
 */
export enum RevitParameterCategory {
  PIPING = 'Piping',
  INSULATION = 'Insulation',
  TRACING = 'Tracing',
  WEIGHT = 'Weight',
  PRESSURE = 'Pressure',
  MATERIAL = 'Material',
  STANDARD = 'Standard',
  GENERAL = 'General',
  ENDPOINTS = 'Endpoints',
}

/**
 * Interface for Revit parameter definition
 */
export interface RevitParameter {
  /** The parameter value/key used in the system */
  value: string;
  /** Display label for the parameter */
  label: string;
  /** Category grouping for the parameter */
  group: RevitParameterCategory;
  /** Optional description of the parameter */
  description?: string;
}

/**
 * Complete list of Revit parameters used in the application
 */
export const REVIT_PARAMETERS: RevitParameter[] = [
  // Piping category
  {
    value: 'PiP_PPG_SPEC',
    label: 'PiP_PPG_SPEC',
    group: RevitParameterCategory.PIPING,
    description: 'Piping specification parameter',
  },

  // Insulation category
  {
    value: 'Insulation Type',
    label: 'Insulation Type',
    group: RevitParameterCategory.INSULATION,
    description: 'Type of insulation applied',
  },
  {
    value: 'Insulation Thickness',
    label: 'Insulation Thickness',
    group: RevitParameterCategory.INSULATION,
    description: 'Thickness of insulation material',
  },
  {
    value: 'INSULATIONTHICKNESS',
    label: 'INSULATIONTHICKNESS',
    group: RevitParameterCategory.INSULATION,
    description: 'Insulation thickness (alternate naming)',
  },

  // Tracing category
  {
    value: 'PiX_GN_2_HeatTracingSpec',
    label: 'PiX_GN_2_HeatTracingSpec',
    group: RevitParameterCategory.TRACING,
    description: 'Heat tracing specification',
  },

  // Weight category
  {
    value: 'PiX_06_Mass_kg_Piping',
    label: 'PiX_06_Mass_kg_Piping',
    group: RevitParameterCategory.WEIGHT,
    description: 'Mass of piping in kilograms',
  },

  // Pressure category
  {
    value: 'PiX_PPG_END1_PressureRating',
    label: 'PiX_PPG_END1_PressureRating',
    group: RevitParameterCategory.PRESSURE,
    description: 'Pressure rating for endpoint 1',
  },
  {
    value: 'PressureClass',
    label: 'PressureClass',
    group: RevitParameterCategory.PRESSURE,
    description: 'Pressure class designation',
  },

  // Material category
  {
    value: 'PiX_03_Material_ENG',
    label: 'PiX_03_Material_ENG',
    group: RevitParameterCategory.MATERIAL,
    description: 'Material specification in English',
  },
  {
    value: 'Material',
    label: 'Material',
    group: RevitParameterCategory.MATERIAL,
    description: 'Generic material parameter',
  },

  // Standard category
  {
    value: 'PiX_02_Standard_ENG',
    label: 'PiX_02_Standard_ENG',
    group: RevitParameterCategory.STANDARD,
    description: 'Design standard in English',
  },
  {
    value: 'DesignStd',
    label: 'DesignStd',
    group: RevitParameterCategory.STANDARD,
    description: 'Design standard designation',
  },

  // General category
  {
    value: 'Description',
    label: 'Description',
    group: RevitParameterCategory.GENERAL,
    description: 'General description field',
  },
  {
    value: 'SKEY',
    label: 'SKEY',
    group: RevitParameterCategory.GENERAL,
    description: 'Symbol key for component identification',
  },
  {
    value: 'OBJECT-TYPE',
    label: 'OBJECT-TYPE',
    group: RevitParameterCategory.GENERAL,
    description: 'Type of object/component',
  },
  {
    value: 'ITEM-DESCRIPTION',
    label: 'ITEM-DESCRIPTION',
    group: RevitParameterCategory.GENERAL,
    description: 'Detailed item description',
  },

  // Endpoints category
  {
    value: 'END-POINT_1',
    label: 'END-POINT_1',
    group: RevitParameterCategory.ENDPOINTS,
    description: 'Primary connector endpoint (IsPrimary)',
  },
  {
    value: 'END-POINT_2',
    label: 'END-POINT_2',
    group: RevitParameterCategory.ENDPOINTS,
    description: 'Secondary connector endpoint (IsSecondary, opposite to port1)',
  },
  {
    value: 'END-POINT_3',
    label: 'END-POINT_3',
    group: RevitParameterCategory.ENDPOINTS,
    description: 'Tertiary endpoint (perpendicular to port1 and port2)',
  },
  {
    value: 'END-POINT_4',
    label: 'END-POINT_4',
    group: RevitParameterCategory.ENDPOINTS,
    description: 'Quaternary endpoint (port number declared in connection description with #)',
  },
];

/**
 * Get parameters filtered by category
 * @param category - The category to filter by
 * @returns Array of parameters matching the category
 */
export function getParametersByCategory(category: RevitParameterCategory): RevitParameter[] {
  return REVIT_PARAMETERS.filter((param) => param.group === category);
}

/**
 * Get a specific parameter by its value/name
 * @param name - The parameter value to search for
 * @returns The matching parameter or undefined if not found
 */
export function getParameterByName(name: string): RevitParameter | undefined {
  return REVIT_PARAMETERS.find((param) => param.value === name);
}

/**
 * Get all unique categories from the parameters
 * @returns Array of unique category values
 */
export function getAllCategories(): RevitParameterCategory[] {
  return Object.values(RevitParameterCategory);
}

/**
 * Get parameters grouped by category
 * @returns Object with categories as keys and arrays of parameters as values
 */
export function getParametersGroupedByCategory(): Record<RevitParameterCategory, RevitParameter[]> {
  const grouped = {} as Record<RevitParameterCategory, RevitParameter[]>;

  for (const category of getAllCategories()) {
    grouped[category] = getParametersByCategory(category);
  }

  return grouped;
}

/**
 * Convert parameters to dropdown options format
 * @param parameters - Optional array of parameters, defaults to all parameters
 * @returns Array of objects with value, label, and group properties
 */
export function toDropdownOptions(parameters: RevitParameter[] = REVIT_PARAMETERS): Array<{
  value: string;
  label: string;
  group: string;
}> {
  return parameters.map((param) => ({
    value: param.value,
    label: param.label,
    group: param.group,
  }));
}
